import React, { useState, useEffect } from 'react';
import api from '../services/api';
import Swal from 'sweetalert2';
import RegistroJornales from './RegistroJornales';
import { obtenerFechaLocal, formatearFechaLegible, calcularDiasTranscurridos } from '../utils/dateUtils';

const GestionEmpleados = ({ proyectoId, proyecto }) => {
  const [empleados, setEmpleados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vistaActual, setVistaActual] = useState('lista'); // 'lista', 'jornales'
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [empleadoEditando, setEmpleadoEditando] = useState(null);

  const [formulario, setFormulario] = useState({
    nombre: '',
    cargo: '',
    tipo_pago: 'por_dia',
    tarifa_dia: '',
    fecha: obtenerFechaLocal(),
    fecha_fin: '',
    activo: true,
    // Nuevos campos para contrato
    valor_contrato: '',
    frecuencia_pago_contrato: 'mensual' // mensual, quincenal, semanal, diario
  });

  useEffect(() => {
    cargarEmpleados();
  }, [proyectoId]);

  const cargarEmpleados = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/proyectos/${proyectoId}/empleados`);
      setEmpleados(response.data);
    } catch (error) {
      console.error('Error al cargar empleados:', error);
      Swal.fire('Error', 'No se pudieron cargar los empleados', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormulario(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Función para calcular diferencia de días entre dos fechas
  const calcularDiferenciaDias = (fechaInicio, fechaFin) => {
    if (!fechaInicio || !fechaFin) return 0;
    
    try {
      // Usar calcularDiasTranscurridos si está disponible, sino calcular manualmente
      const inicio = new Date(fechaInicio);
      const fin = new Date(fechaFin);
      
      // Calcular diferencia en milisegundos y convertir a días
      const diferenciaMs = fin.getTime() - inicio.getTime();
      const diferenciaDias = Math.ceil(diferenciaMs / (1000 * 60 * 60 * 24));
      
      return Math.max(diferenciaDias, 1); // Mínimo 1 día
    } catch (error) {
      console.error('Error calculando diferencia de días:', error);
      return 0;
    }
  };

  // Calcular tarifa diaria basada en contrato
  const calcularTarifaDiariaDesdeContrato = () => {
    if (formulario.tipo_pago !== 'por_contrato' || !formulario.valor_contrato || !formulario.fecha || !formulario.fecha_fin) {
      return '';
    }

    const diasContrato = calcularDiferenciaDias(formulario.fecha, formulario.fecha_fin);
    if (diasContrato <= 0) return '';

    const valorContrato = parseFloat(formulario.valor_contrato);
    const tarifaDiaria = valorContrato / diasContrato;
    
    return tarifaDiaria.toFixed(2);
  };

  // Calcular pago por período según frecuencia
  const calcularPagoPorPeriodo = () => {
    if (formulario.tipo_pago !== 'por_contrato' || !formulario.valor_contrato || !formulario.fecha || !formulario.fecha_fin) {
      return { valor: 0, label: '' };
    }

    const valorContrato = parseFloat(formulario.valor_contrato);
    const diasContrato = calcularDiferenciaDias(formulario.fecha, formulario.fecha_fin);
    const mesesContrato = diasContrato / 30.44; // Promedio de días por mes

    let valorPeriodo = 0;
    let label = '';

    switch(formulario.frecuencia_pago_contrato) {
      case 'mensual':
        valorPeriodo = valorContrato / mesesContrato;
        label = `RD$ ${valorPeriodo.toLocaleString('es-DO', { minimumFractionDigits: 2 })} / mes`;
        break;
      case 'quincenal':
        valorPeriodo = valorContrato / (mesesContrato * 2);
        label = `RD$ ${valorPeriodo.toLocaleString('es-DO', { minimumFractionDigits: 2 })} / quincena`;
        break;
      case 'semanal':
        valorPeriodo = valorContrato / (mesesContrato * 4.33);
        label = `RD$ ${valorPeriodo.toLocaleString('es-DO', { minimumFractionDigits: 2 })} / semana`;
        break;
      case 'diario':
        valorPeriodo = valorContrato / diasContrato;
        label = `RD$ ${valorPeriodo.toLocaleString('es-DO', { minimumFractionDigits: 2 })} / día`;
        break;
      default:
        valorPeriodo = valorContrato / mesesContrato;
        label = `RD$ ${valorPeriodo.toLocaleString('es-DO', { minimumFractionDigits: 2 })} / mes`;
    }

    return { valor: valorPeriodo, label };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Campos obligatorios base
    if (!formulario.nombre || !formulario.fecha) {
      Swal.fire('Error', 'Por favor completa los campos obligatorios', 'warning');
      return;
    }

    // Validación según tipo de pago
    if (formulario.tipo_pago === 'por_contrato') {
      if (!formulario.valor_contrato || !formulario.fecha_fin) {
        Swal.fire('Error', 'Para contrato debe especificar valor total y fecha de fin', 'warning');
        return;
      }
    } else {
      if (!formulario.tarifa_dia) {
        Swal.fire('Error', 'Por favor ingresa la tarifa', 'warning');
        return;
      }
    }

    // Validar que fecha_fin sea posterior a fecha
    if (formulario.fecha_fin && formulario.fecha > formulario.fecha_fin) {
      Swal.fire('Error', 'La fecha de fin debe ser posterior a la fecha de inicio', 'warning');
      return;
    }

    // Preparar datos para enviar
    const datosEnviar = { ...formulario };
    
    // Si es por contrato, calcular tarifa diaria para consistencia en el sistema
    if (formulario.tipo_pago === 'por_contrato') {
      const tarifaDiariaCalculada = calcularTarifaDiariaDesdeContrato();
      datosEnviar.tarifa_dia = tarifaDiariaCalculada || '0';
    }

    try {
      if (empleadoEditando) {
        await api.put(`/empleados/${empleadoEditando.id}`, datosEnviar);
        Swal.fire('✅ Actualizado', 'Empleado actualizado correctamente', 'success');
      } else {
        await api.post(`/proyectos/${proyectoId}/empleados`, datosEnviar);
        Swal.fire('✅ Registrado', 'Empleado registrado correctamente', 'success');
      }

      limpiarFormulario();
      cargarEmpleados();
      setMostrarFormulario(false);
    } catch (error) {
      console.error('Error al guardar empleado:', error);
      Swal.fire('Error', 'No se pudo guardar el empleado', 'error');
    }
  };

  const editarEmpleado = (empleado) => {
    setEmpleadoEditando(empleado);
    setFormulario({
      nombre: empleado.nombre,
      cargo: empleado.cargo || '',
      tipo_pago: empleado.tipo_pago || 'por_dia',
      tarifa_dia: empleado.tarifa_dia || '',
      valor_contrato: empleado.valor_contrato || '',
      frecuencia_pago_contrato: empleado.frecuencia_pago_contrato || 'mensual',
      fecha: empleado.fecha_inicio,
      fecha_fin: empleado.fecha_fin || '',
      activo: empleado.activo
    });
    setMostrarFormulario(true);
  };

  const cambiarEstadoEmpleado = async (empleado) => {
    const nuevoEstado = !empleado.activo;
    const accion = nuevoEstado ? 'activar' : 'desactivar';

    const result = await Swal.fire({
      title: `¿${accion.charAt(0).toUpperCase() + accion.slice(1)} empleado?`,
      text: `${empleado.nombre} será ${nuevoEstado ? 'activado' : 'desactivado'}`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: nuevoEstado ? '#10b981' : '#f59e0b',
      cancelButtonColor: '#64748b',
      confirmButtonText: `Sí, ${accion}`,
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      try {
        await api.put(`/empleados/${empleado.id}`, { activo: nuevoEstado });
        Swal.fire('✅ Actualizado', `Empleado ${nuevoEstado ? 'activado' : 'desactivado'} correctamente`, 'success');
        cargarEmpleados();
      } catch (error) {
        console.error('Error al cambiar estado:', error);
        Swal.fire('Error', 'No se pudo actualizar el estado', 'error');
      }
    }
  };

  const limpiarFormulario = () => {
    setFormulario({
      nombre: '',
      cargo: '',
      tipo_pago: 'por_dia',
      tarifa_dia: '',
      valor_contrato: '',
      frecuencia_pago_contrato: 'mensual',
      fecha: obtenerFechaLocal(),
      fecha_fin: '',
      activo: true
    });
    setEmpleadoEditando(null);
  };

  const formatMoney = (valor) => {
    return `RD$ ${parseFloat(valor || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatFecha = (fecha) => {
    if (!fecha) return 'N/A';
    return formatearFechaLegible(fecha);
  };

  // Calcular nómina diaria - ahora incluye contratos convertidos a diario
  const calcularNominaDiariaEstimada = () => {
    return empleadosActivos.reduce((sum, empleado) => {
      return sum + parseFloat(empleado.tarifa_dia || 0);
    }, 0);
  };

  // Formatear información de contrato para mostrar
  const formatInfoContrato = (empleado) => {
    if (empleado.tipo_pago !== 'por_contrato') return '';
    
    const valor = parseFloat(empleado.valor_contrato || 0);
    const frecuencia = empleado.frecuencia_pago_contrato || 'mensual';
    
    const frecuencias = {
      mensual: '/mes',
      quincenal: '/quincena',
      semanal: '/semana',
      diario: '/día'
    };
    
    return `Contrato: ${formatMoney(valor)} (${frecuencias[frecuencia] || ''})`;
  };

  const empleadosActivos = empleados.filter(e => e.activo);
  const empleadosInactivos = empleados.filter(e => !e.activo);

  // Calcular resumen de contratos
  const empleadosContrato = empleadosActivos.filter(e => e.tipo_pago === 'por_contrato');
  const totalContratos = empleadosContrato.reduce((sum, e) => {
  // Forzamos la lectura del campo que acabas de crear en PostgreSQL
  const monto = parseFloat(e.valor_contrato || 0);
  console.log(`Sumando contrato de ${e.nombre}: ${monto}`); // Para que verifiques en consola
  return sum + monto;
}, 0);
  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>👥 Gestión de Empleados</h2>
          <p style={styles.subtitle}>{proyecto?.nombre}</p>
        </div>
        <button
          onClick={() => {
            limpiarFormulario();
            setMostrarFormulario(!mostrarFormulario);
          }}
          style={styles.btnPrimary}
        >
          {mostrarFormulario ? '✕ Cancelar' : '+ Registrar Empleado'}
        </button>
      </div>

      {/* Tabs */}
      <div style={styles.tabs}>
        <button
          style={{
            ...styles.tab,
            ...(vistaActual === 'lista' ? styles.tabActive : {})
          }}
          onClick={() => setVistaActual('lista')}
        >
          👥 Empleados
        </button>
        <button
          style={{
            ...styles.tab,
            ...(vistaActual === 'jornales' ? styles.tabActive : {})
          }}
          onClick={() => setVistaActual('jornales')}
        >
          📅 Jornales
        </button>
      </div>

      {vistaActual === 'lista' && (
        <>
          {/* Resumen */}
          <div style={styles.resumen}>
            <div style={styles.resumenCard}>
              <div style={styles.resumenIcon}>👥</div>
              <div>
                <p style={styles.resumenLabel}>Empleados Activos</p>
                <h3 style={styles.resumenValue}>{empleadosActivos.length}</h3>
              </div>
            </div>
            <div style={styles.resumenCard}>
              <div style={styles.resumenIcon}>💰</div>
              <div>
                <p style={styles.resumenLabel}>Nómina Diaria</p>
                <h3 style={styles.resumenValue}>
                  {formatMoney(calcularNominaDiariaEstimada())}
                </h3>
              </div>
            </div>
            {empleadosContrato.length > 0 && (
              <div style={styles.resumenCard}>
                <div style={styles.resumenIcon}>📄</div>
                <div>
                  <p style={styles.resumenLabel}>Contratos Activos</p>
                  <h3 style={styles.resumenValue}>
                    {formatMoney(totalContratos)}
                    <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 'normal', marginLeft: '5px' }}>
                      ({empleadosContrato.length} contratos)
                    </span>
                  </h3>
                </div>
              </div>
            )}
          </div>

          {/* Formulario */}
          {mostrarFormulario && (
            <div style={styles.formulario}>
              <h3 style={styles.formTitle}>
                {empleadoEditando ? '✏️ Editar Empleado' : '➕ Nuevo Empleado'}
              </h3>

              <form onSubmit={handleSubmit}>
                <div style={styles.formGrid}>
                  {/* Nombre */}
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Nombre Completo *</label>
                    <input
                      type="text"
                      name="nombre"
                      value={formulario.nombre}
                      onChange={handleChange}
                      placeholder="Nombre del empleado"
                      style={styles.input}
                      required
                    />
                  </div>

                  {/* Cargo */}
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Cargo / Posición</label>
                    <input
                      type="text"
                      name="cargo"
                      value={formulario.cargo}
                      onChange={handleChange}
                      placeholder="Ej: Maestro Constructor, Ayudante, etc."
                      style={styles.input}
                    />
                  </div>

                  {/* Tipo de Pago */}
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Tipo de Pago *</label>
                    <select
                      name="tipo_pago"
                      value={formulario.tipo_pago}
                      onChange={handleChange}
                      style={styles.select}
                      required
                    >
                      <option value="por_dia">Por Día</option>
                      <option value="por_hora">Por Hora</option>
                      <option value="por_contrato">Por Contrato</option>
                    </select>
                  </div>

                  {/* Campos según tipo de pago */}
                  {formulario.tipo_pago === 'por_contrato' ? (
                    <>
                      {/* Valor del Contrato */}
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Valor Total del Contrato (RD$) *</label>
                        <input
                          type="number"
                          name="valor_contrato"
                          value={formulario.valor_contrato}
                          onChange={handleChange}
                          placeholder="0.00"
                          step="0.01"
                          min="0"
                          style={styles.input}
                          required={formulario.tipo_pago === 'por_contrato'}
                        />
                      </div>

                      {/* Frecuencia de Pago para Contrato */}
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Frecuencia de Pago *</label>
                        <select
                          name="frecuencia_pago_contrato"
                          value={formulario.frecuencia_pago_contrato}
                          onChange={handleChange}
                          style={styles.select}
                          required={formulario.tipo_pago === 'por_contrato'}
                        >
                          <option value="mensual">Mensual</option>
                          <option value="quincenal">Quincenal</option>
                          <option value="semanal">Semanal</option>
                          <option value="diario">Diario</option>
                        </select>
                      </div>

                      {/* Información de cálculo */}
                      {formulario.valor_contrato && formulario.fecha && formulario.fecha_fin && (
                        <div style={{...styles.formGroup, gridColumn: '1 / -1', padding: '10px', background: '#f0f9ff', borderRadius: '6px'}}>
                          <p style={{ margin: '0 0 5px 0', fontSize: '13px', color: '#0369a1' }}>
                            <strong>Información de pago:</strong>
                          </p>
                          <p style={{ margin: '0', fontSize: '12px', color: '#64748b' }}>
                            {calcularPagoPorPeriodo().label}
                          </p>
                        </div>
                      )}
                    </>
                  ) : (
                    /* Tarifa normal para día o hora */
                    <div style={styles.formGroup}>
                      <label style={styles.label}>
                        Tarifa {formulario.tipo_pago === 'por_dia' ? 'Diaria' : 'por Hora'} (RD$) *
                      </label>
                      <input
                        type="number"
                        name="tarifa_dia"
                        value={formulario.tarifa_dia}
                        onChange={handleChange}
                        placeholder="0.00"
                        step="0.01"
                        min="0"
                        style={styles.input}
                        required={formulario.tipo_pago !== 'por_contrato'}
                      />
                    </div>
                  )}

                  {/* Fecha Inicio */}
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Fecha de Inicio *</label>
                    <input
                      type="date"
                      name="fecha"
                      value={formulario.fecha}
                      onChange={handleChange}
                      style={styles.input}
                      required
                    />
                  </div>

                  {/* Fecha Fin - Obligatoria para contrato */}
                  <div style={styles.formGroup}>
                    <label style={styles.label}>
                      Fecha de Fin {formulario.tipo_pago === 'por_contrato' ? '*' : '(Opcional)'}
                    </label>
                    <input
                      type="date"
                      name="fecha_fin"
                      value={formulario.fecha_fin}
                      onChange={handleChange}
                      style={styles.input}
                      required={formulario.tipo_pago === 'por_contrato'}
                    />
                  </div>

                  {/* Estado */}
                  <div style={{...styles.formGroup, gridColumn: '1 / -1'}}>
                    <label style={styles.checkboxLabel}>
                      <input
                        type="checkbox"
                        name="activo"
                        checked={formulario.activo}
                        onChange={handleChange}
                        style={styles.checkbox}
                      />
                      <span>Empleado activo</span>
                    </label>
                  </div>
                </div>

                {/* Botones */}
                <div style={styles.formActions}>
                  <button
                    type="button"
                    onClick={() => {
                      limpiarFormulario();
                      setMostrarFormulario(false);
                    }}
                    style={styles.btnSecondary}
                  >
                    Cancelar
                  </button>
                  <button type="submit" style={styles.btnSubmit}>
                    {empleadoEditando ? '💾 Actualizar' : '💾 Guardar Empleado'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Lista de Empleados */}
          {loading ? (
            <div style={styles.loading}>
              <div style={styles.spinner}></div>
              <p>Cargando empleados...</p>
            </div>
          ) : empleados.length === 0 ? (
            <div style={styles.empty}>
              <div style={styles.emptyIcon}>👷</div>
              <h3>No hay empleados registrados</h3>
              <p>Comienza registrando el primer empleado del proyecto</p>
            </div>
          ) : (
            <>
              {/* Empleados Activos */}
              {empleadosActivos.length > 0 && (
                <>
                  <h3 style={styles.sectionTitle}>✅ Empleados Activos ({empleadosActivos.length})</h3>
                  <div style={styles.listaEmpleados}>
                    {empleadosActivos.map((empleado) => (
                      <div key={empleado.id} style={styles.empleadoCard}>
                        <div style={styles.empleadoHeader}>
                          <div>
                            <h4 style={styles.empleadoNombre}>{empleado.nombre}</h4>
                            {empleado.cargo && (
                              <p style={styles.empleadoCargo}>{empleado.cargo}</p>
                            )}
                            {empleado.tipo_pago === 'por_contrato' && (
                              <div>
                                <span style={{
                                  display: 'inline-block',
                                  backgroundColor: '#e0f2fe',
                                  color: '#0369a1',
                                  padding: '2px 8px',
                                  borderRadius: '4px',
                                  fontSize: '11px',
                                  fontWeight: '600',
                                  marginTop: '4px'
                                }}>
                                  📄 CONTRATO
                                </span>
                                <p style={{
                                  fontSize: '12px',
                                  color: '#475569',
                                  margin: '4px 0 0 0'
                                }}>

                                {/* 🛠️ MEJORA: Si valor_contrato es 0, intentamos mostrar tarifa_dia acumulada o un mensaje */}
      {parseFloat(empleado.valor_contrato) > 0 
        ? formatInfoContrato(empleado) 
        : `Tarifa diaria: ${formatMoney(empleado.tarifa_dia)}`}
    </p>
  </div>
)}
                          </div>
                          <div style={styles.empleadoTarifa}>
                            {empleado.tipo_pago === 'por_contrato' ? (
                              <>
                                <div>{formatMoney(empleado.tarifa_dia)}</div>
                                <span style={styles.tarifaTipo}>/día</span>
                              </>
                            ) : (
                              <>
                                <div>{formatMoney(empleado.tarifa_dia)}</div>
                                <span style={styles.tarifaTipo}>
                                  /{empleado.tipo_pago === 'por_dia' ? 'día' : 'hora'}
                                </span>
                              </>
                            )}
                          </div>
                        </div>

                        <div style={styles.empleadoInfo}>
                          <div style={styles.infoItem}>
                            <span style={styles.infoLabel}>📅 Inicio:</span>
                            <span>{formatFecha(empleado.fecha_inicio)}</span>
                          </div>
                          {empleado.fecha_fin && (
                            <div style={styles.infoItem}>
                              <span style={styles.infoLabel}>🏁 Fin:</span>
                              <span>{formatFecha(empleado.fecha_fin)}</span>
                            </div>
                          )}
                          <div style={styles.infoItem}>
                            <span style={styles.infoLabel}>💼 Tipo:</span>
                            <span>
                              {empleado.tipo_pago === 'por_dia' ? 'Por Día' : 
                               empleado.tipo_pago === 'por_hora' ? 'Por Hora' : 'Por Contrato'}
                            </span>
                          </div>
                          {empleado.tipo_pago === 'por_contrato' && empleado.frecuencia_pago_contrato && (
                            <div style={styles.infoItem}>
                              <span style={styles.infoLabel}>⏱️ Frecuencia:</span>
                              <span>
                                {empleado.frecuencia_pago_contrato === 'mensual' ? 'Mensual' :
                                 empleado.frecuencia_pago_contrato === 'quincenal' ? 'Quincenal' :
                                 empleado.frecuencia_pago_contrato === 'semanal' ? 'Semanal' : 'Diario'}
                              </span>
                            </div>
                          )}
                        </div>

                        <div style={styles.empleadoActions}>
                          <button
                            onClick={() => editarEmpleado(empleado)}
                            style={styles.btnEdit}
                          >
                            ✏️ Editar
                          </button>
                          <button
                            onClick={() => cambiarEstadoEmpleado(empleado)}
                            style={styles.btnDesactivar}
                          >
                            ⏸️ Desactivar
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Empleados Inactivos */}
              {empleadosInactivos.length > 0 && (
                <>
                  <h3 style={{...styles.sectionTitle, marginTop: '30px'}}>
                    ⏸️ Empleados Inactivos ({empleadosInactivos.length})
                  </h3>
                  <div style={styles.listaEmpleados}>
                    {empleadosInactivos.map((empleado) => (
                      <div key={empleado.id} style={{...styles.empleadoCard, opacity: 0.7}}>
                        <div style={styles.empleadoHeader}>
                          <div>
                            <h4 style={styles.empleadoNombre}>{empleado.nombre}</h4>
                            {empleado.cargo && (
                              <p style={styles.empleadoCargo}>{empleado.cargo}</p>
                            )}
                          </div>
                          <div style={styles.empleadoTarifa}>
                            {formatMoney(empleado.tarifa_dia)}
                            <span style={styles.tarifaTipo}>
                              /{empleado.tipo_pago === 'por_dia' ? 'día' : 'hora'}
                            </span>
                          </div>
                        </div>

                        <div style={styles.empleadoActions}>
                          <button
                            onClick={() => editarEmpleado(empleado)}
                            style={styles.btnEdit}
                          >
                            ✏️ Editar
                          </button>
                          <button
                            onClick={() => cambiarEstadoEmpleado(empleado)}
                            style={styles.btnActivar}
                          >
                            ▶️ Activar
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </>
      )}

     {vistaActual === 'jornales' && (
  <RegistroJornales proyectoId={proyectoId} proyecto={proyecto} />
)}
    </div>
  );
};

const styles = {
  container: {
    padding: '20px'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '20px',
    flexWrap: 'wrap',
    gap: '15px'
  },
  title: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#1e293b',
    margin: '0 0 5px 0'
  },
  subtitle: {
    fontSize: '14px',
    color: '#64748b',
    margin: 0
  },
  btnPrimary: {
    padding: '12px 24px',
    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
  },
  tabs: {
    display: 'flex',
    gap: '10px',
    marginBottom: '20px',
    borderBottom: '2px solid #e2e8f0'
  },
  tab: {
    padding: '12px 24px',
    background: 'none',
    border: 'none',
    borderBottom: '3px solid transparent',
    cursor: 'pointer',
    fontSize: '15px',
    fontWeight: '600',
    color: '#64748b',
    transition: 'all 0.2s'
  },
  tabActive: {
    color: '#3b82f6',
    borderBottom: '3px solid #3b82f6'
  },
  resumen: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '15px',
    marginBottom: '20px'
  },
  resumenCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    background: 'white',
    padding: '20px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    border: '1px solid #e2e8f0'
  },
  resumenIcon: {
    fontSize: '36px'
  },
  resumenLabel: {
    fontSize: '13px',
    color: '#64748b',
    margin: '0 0 5px 0',
    fontWeight: '600'
  },
  resumenValue: {
    fontSize: '22px',
    fontWeight: '700',
    color: '#1e293b',
    margin: 0
  },
  formulario: {
    background: 'white',
    padding: '25px',
    borderRadius: '12px',
    marginBottom: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    border: '1px solid #e2e8f0'
  },
  formTitle: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: '20px'
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '15px',
    marginBottom: '20px'
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column'
  },
  label: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#475569',
    marginBottom: '6px'
  },
  input: {
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid #cbd5e1',
    borderRadius: '8px',
    outline: 'none'
  },
  select: {
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid #cbd5e1',
    borderRadius: '8px',
    background: 'white',
    cursor: 'pointer'
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#475569',
    cursor: 'pointer'
  },
  checkbox: {
    width: '18px',
    height: '18px',
    cursor: 'pointer'
  },
  formActions: {
    display: 'flex',
    gap: '10px',
    justifyContent: 'flex-end'
  },
  btnSecondary: {
    padding: '10px 20px',
    background: '#f1f5f9',
    color: '#64748b',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  btnSubmit: {
    padding: '10px 20px',
    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
  },
  loading: {
    textAlign: 'center',
    padding: '40px'
  },
  spinner: {
    border: '4px solid #f3f4f6',
    borderTop: '4px solid #3b82f6',
    borderRadius: '50%',
    width: '40px',
    height: '40px',
    animation: 'spin 0.8s linear infinite',
    margin: '0 auto 15px'
  },
  empty: {
    textAlign: 'center',
    padding: '60px 20px',
    background: 'white',
    borderRadius: '12px',
    border: '2px dashed #cbd5e1'
  },
  emptyIcon: {
    fontSize: '64px',
    marginBottom: '15px'
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: '15px'
  },
  listaEmpleados: {
    display: 'grid',
    gap: '15px',
    marginBottom: '20px'
  },
  empleadoCard: {
    background: 'white',
    padding: '20px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    border: '1px solid #e2e8f0'
  },
  empleadoHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '15px',
    gap: '15px'
  },
  empleadoNombre: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#1e293b',
    margin: '0 0 4px 0'
  },
  empleadoCargo: {
    fontSize: '13px',
    color: '#64748b',
    margin: 0
  },
  empleadoTarifa: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#3b82f6',
    textAlign: 'right'
  },
  tarifaTipo: {
    fontSize: '12px',
    color: '#64748b',
    fontWeight: '500'
  },
  empleadoInfo: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '10px',
    marginBottom: '15px'
  },
  infoItem: {
    fontSize: '13px',
    color: '#475569'
  },
  infoLabel: {
    fontWeight: '600',
    marginRight: '5px'
  },
  empleadoActions: {
    display: 'flex',
    gap: '10px',
    paddingTop: '15px',
    borderTop: '1px solid #e2e8f0'
  },
  btnEdit: {
    flex: 1,
    padding: '8px 16px',
    background: '#f0f9ff',
    color: '#0369a1',
    border: '1px solid #bae6fd',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  btnDesactivar: {
    flex: 1,
    padding: '8px 16px',
    background: '#fef9ec',
    color: '#d97706',
    border: '1px solid #fde68a',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  btnActivar: {
    flex: 1,
    padding: '8px 16px',
    background: '#f0fdf4',
    color: '#16a34a',
    border: '1px solid #bbf7d0',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  section: {
    marginTop: '20px'
  }
};

export default GestionEmpleados;